/**
 * usePermissions Hook
 * Custom hook for checking user permissions
 */
import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getUserPermissions,
} from '../utils/permissions';

/**
 * Hook for permission checking
 * @returns {Object} Permission checking methods
 */
export const usePermissions = () => {
  const { user } = useAuth();

  /**
   * Check if user has specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has permission
   */
  const checkPermission = useCallback(
    (permission) => {
      return hasPermission(user, permission);
    },
    [user]
  );

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissions - Array of permissions
   * @returns {boolean} True if user has any permission
   */
  const checkAnyPermission = useCallback(
    (permissions) => {
      return hasAnyPermission(user, permissions);
    },
    [user]
  );

  /**
   * Check if user has all specified permissions
   * @param {string[]} permissions - Array of permissions
   * @returns {boolean} True if user has all permissions
   */
  const checkAllPermissions = useCallback(
    (permissions) => {
      return hasAllPermissions(user, permissions);
    },
    [user]
  );

  /**
   * Get all user permissions
   * @returns {string[]} Array of permissions
   */
  const userPermissions = useMemo(() => {
    return getUserPermissions(user);
  }, [user]);

  return {
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    userPermissions,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    role: user?.role,
  };
};

export default usePermissions;
